import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-3-3：三位數 × 一位數直式計算。
 * difficulty 1：100-299 × 2-5（結果較小）
 * difficulty 2：200-599 × 3-7
 * difficulty 3：500-999 × 5-9
 */
export function generateMul3x1(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  let aMin: number, aMax: number, bMin: number, bMax: number;
  if (difficulty === 1) {
    [aMin, aMax, bMin, bMax] = [100, 299, 2, 5];
  } else if (difficulty === 2) {
    [aMin, aMax, bMin, bMax] = [200, 599, 3, 7];
  } else {
    [aMin, aMax, bMin, bMax] = [500, 999, 5, 9];
  }

  const a = randInt(rand, aMin, aMax);
  const b = randInt(rand, bMin, bMax);
  const correct = a * b;

  // Distractors: typical mistakes for 3×1 multiplication
  const distractors = new Set<number>();

  // 1. Forgot the carry from ones to tens
  const onesProduct = (a % 10) * b;
  const tensProduct = (Math.floor(a / 10) % 10) * b;
  const hundProduct = Math.floor(a / 100) * b;
  const carry1 = Math.floor(onesProduct / 10);
  const noCarryOnes = hundProduct * 100 + tensProduct * 10 + (onesProduct % 10);
  if (noCarryOnes !== correct && noCarryOnes > 0) distractors.add(noCarryOnes);

  // 2. Forgot carry from tens to hundreds
  const onesDigit = onesProduct % 10;
  const tensPlusCarry = tensProduct + carry1;
  const carry2 = Math.floor(tensPlusCarry / 10);
  const noCarryTens = hundProduct * 100 + tensProduct * 10 + onesDigit;
  if (noCarryTens !== correct && noCarryTens > 0) distractors.add(noCarryTens);
  void carry2; // used in explanation only

  // 3. Off-by-one digit: multiply only last two digits of a
  const twoDigitOnly = (a % 100) * b;
  if (twoDigitOnly !== correct && twoDigitOnly > 0) distractors.add(twoDigitOnly);

  // Fill remaining with nearby values
  while (distractors.size < 3) {
    const offset = randInt(rand, 1, 50) * (rand() > 0.5 ? 1 : -1);
    const candidate = correct + offset;
    if (candidate > 0 && candidate !== correct && !distractors.has(candidate)) {
      distractors.add(candidate);
    }
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  const shuffled = [...optionList].sort((x, y) => mulberry32(seed + x)() - mulberry32(seed + y)());

  // Step-by-step explanation
  const step1 = onesProduct;
  const step2 = tensPlusCarry;
  const step3 = hundProduct + Math.floor(tensPlusCarry / 10);

  return {
    kind: 'mcq',
    prompt: `${a} × ${b} = ?`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `個位 ${a % 10}×${b}=${step1}；十位 ${Math.floor(a / 10) % 10}×${b}+進位${carry1}=${step2}；百位 ${Math.floor(a / 100)}×${b}+進位${Math.floor(tensPlusCarry / 10)}=${step3} → ${correct}`,
    kp: 'math.mul.3x1',
  };
}
